/**
 * অবস্থা ৮ · অনুমতি নেই — `P0 States § ১০` (Permission denied). `E_PERMISSION`.
 *
 * Frame note: "Names the user's actual role and lists what that role does reach, so
 * refusal teaches the permission model. Requesting access is logged — §08 of the
 * architecture, in the one screen where it is felt."
 *
 * ── W1-04 · ONLY WHERE ROLES DIFFER, AND ONLY WHAT THE REFUSAL SAYS ──────────────
 *
 * WHERE. This frame used to be reachable on অ্যাকাউন্ট, and that is how a signed-out
 * customer opening their own account page came to be told, on founder mode's dark ground,
 * that they were in the finance department and that the cap table was for founders. A
 * customer has one role; there is nothing for the customer app to refuse by role, and a
 * missing session is not a refusal at all — it is `auth/SignInPrompt.tsx`. So the matrix
 * now carries this state on অভ্যন্তরীণ alone (`matrix.ts`), and the boundary never draws it
 * for a request that had no identity (`boundary.tsx`).
 *
 * WHAT. The drawn frame is one refusal, of one person, in one place, and it said so in
 * every line: the header "ফাউন্ডার মোড" over "অপ্রাপ্য লিমিটেড", a body naming the cap table
 * and the finance role, four chips of what finance can see, a request "that will be
 * logged" with no endpoint behind it, and "back to the finance dashboard". Shown to
 * anybody else — an ops agent refused a vendor's payout, an HR lead refused a ledger — each
 * line was false. Now:
 *
 *   header   `header_bn` from the refusal, or no title — never a mode the caller is not in
 *   title    `title_bn` from the refusal, or the drawn generic line, which is true of
 *            every role refusal ("এই তথ্য আপনার ভূমিকায় দেখা যায় না")
 *   body     `body_bn` from the refusal, else `message_bn`, else nothing (see below)
 *   ways out the back chevron, and a request control ONLY when a handler is given — which
 *            nothing gives until an endpoint records access requests
 *
 * `docs/deviations/W1-04.md` (S10) records what this costs the gate.
 *
 * ── AN EMPTY SENTENCE IS NOT AN ABSENT ONE, STILL ────────────────────────────────
 * `bodyBn === ''` is the server refusing with no sentence (`copy_status: 'copy_missing'`),
 * and renders nothing. So does `undefined` now: the drawn body was role-specific, and
 * there is no longer a drawn sentence for either value to fall back to. The trap the
 * previous note warned about — `||` turning an empty refusal into the finance department —
 * is closed by construction: there is nothing left to fall back to.
 *
 * The frame is `background:#0A2E37;border:1px solid #0A2E37` — teal-dark on teal-dark,
 * transcribed as drawn rather than collapsed to a borderless box, because the border
 * is a real 1px the layout accounts for. This is still the only state drawn on the dark
 * ground; it is an internal surface's state and the dark ground is the consoles'.
 */
import { ariaLabel } from '@oprappo/tokens';
import { StateFrame } from './chrome.js';
import { PERMISSION_DENIED } from './data.js';

export interface PermissionDeniedStateProps {
  /** `body_bn` or `message_bn` from the refusal. Absent or empty: nothing is drawn. */
  bodyBn?: string | undefined;
  /** `title_bn` from the refusal. Absent: the drawn generic refusal line. */
  titleBn?: string | undefined;
  /** `header_bn` from the refusal — where it happened. Absent: no title in the bar. */
  headerBn?: string | undefined;
  /**
   * Ask for access. The control is drawn ONLY when this is given: a button that says the
   * request "will be logged" and records nothing is worse than no button.
   */
  onRequest?: (() => void) | undefined;
  /** The label for `onRequest`'s control. Required with it; never invented here. */
  requestBn?: string | undefined;
  onBack?: (() => void) | undefined;
}

function said(value: string | undefined): value is string {
  return value !== undefined && value.trim() !== '';
}

/** `rect x=5 y=10 w=14 h=10 rx=1.5` plus the shackle, amber, 2px. */
function LockGlyph({ size }: { size: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none">
      <rect x="5" y="10" width="14" height="10" rx="1.5" stroke="#E8A33D" strokeWidth={2} />
      <path d="M8.5 10V7a3.5 3.5 0 017 0v3" stroke="#E8A33D" strokeWidth={2} />
    </svg>
  );
}

export function PermissionDeniedState({
  bodyBn,
  titleBn,
  headerBn,
  onRequest,
  requestBn,
  onBack,
}: PermissionDeniedStateProps = {}): JSX.Element {
  const d = PERMISSION_DENIED;
  return (
    <StateFrame stateKey="permission_denied" className="bg-teal-dark border border-teal-dark">
      <div className="border-b border-white/[.14] px-[16px] py-[14px] flex gap-[12px] items-center">
        <button
          type="button"
          aria-label={ariaLabel.back}
          onClick={onBack}
          className="bg-transparent border-0 p-0 rounded-none flex-none"
        >
          <svg width={20} height={20} viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M15 5l-7 7 7 7" stroke="#8FB0AE" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="flex-1">
          {said(headerBn) ? (
            <div className="font-anek font-semibold text-[16px] leading-[1.75] text-white">{headerBn}</div>
          ) : null}
        </div>
        {/* The § 01 accessible name for a lock is `সুরক্ষিত এলাকা`. */}
        <span role="img" aria-label={ariaLabel.lock} className="flex-none">
          <LockGlyph size={18} />
        </span>
      </div>

      <div className="flex-1 px-[16px] py-[20px] flex flex-col">
        <div className="box-content w-[56px] h-[56px] border-2 border-amber flex items-center justify-center mb-[22px]">
          <LockGlyph size={26} />
        </div>
        {/* 22px Bangla sits in the display band: leading 1.25, not 1.75 (docs/02). */}
        <div className="font-anek font-semibold text-[22px] leading-[1.25] text-white">
          {said(titleBn) ? titleBn : d.title_bn}
        </div>
        {said(bodyBn) ? (
          <div className="font-anek font-normal text-[15px] leading-[1.75] text-on-teal-body mt-[10px]">{bodyBn}</div>
        ) : null}

        <div className="flex-1" />

        {onRequest !== undefined && said(requestBn) ? (
          <button
            type="button"
            onClick={onRequest}
            className="box-content border-[1.5px] border-amber h-[50px] p-0 flex items-center justify-center font-anek font-semibold text-[15px] leading-[1.75] text-amber mt-[11px] bg-transparent rounded-none"
          >
            {requestBn}
          </button>
        ) : null}
      </div>
    </StateFrame>
  );
}
